<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="InquiryList" label="Inquiries" :active="true" />
    </breadcrumb-list>
    <section>
      <div class="container is-max-hd">
        <div class="box">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <h1 class="title">Inquiry List</h1>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item filter-select-item">
                <b-field expanded>
                  <b-select
                    v-model="selectedUserID"
                    placeholder="Show Inquiries for User"
                    :loading="loading"
                    @input="handleSelect()"
                    expanded
                  >
                    <option :value="0">All Users</option>
                    <option v-for="opt in userOptions" :value="opt.id" :key="opt.id">
                      {{ opt.first_name }} {{ opt.last_name }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="level-item filter-select-item">
                <b-field expanded>
                  <b-select
                    v-model="selectedProviderID"
                    placeholder="Show Inquiries for Provider"
                    :loading="loading"
                    @input="handleSelect()"
                    expanded
                  >
                    <option :value="0">All Providers</option>
                    <option v-for="opt in providerOptions" :value="opt.id" :key="opt.id">
                      {{ opt.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="level-item">
                <div class="subscription-wrapper">
                  <b-button
                    type="is-primary"
                    :disabled="!canCreateInquiry"
                    @click="canCreateInquiry ? $router.push({ name: 'InquiryCreate' }) : null"
                  >
                    Create New Inquiry
                  </b-button>

                  <div
                    v-if="!loading && user && !subscriptionNoticeClosed && inquiryNoticeMessage"
                    class="subscription-notice"
                  >
                    <button class="notice-close-btn" @click="closeSubscriptionNotice" type="button">
                      <b-icon icon="close" size="is-small"></b-icon>
                    </button>
                    <p class="has-text-danger has-text-weight-semibold mb-2">
                      {{ inquiryNoticeMessage }}
                    </p>
                    <div class="subscription-actions">
                      <b-button
                        type="is-primary"
                        size="is-small"
                        @click="isPastDue ? handleUpdatePayment() : goToSubscriptionPage()"
                      >
                        {{ isPastDue ? 'Update Payment' : 'Enable Feature' }}
                      </b-button>
                      <span class="mx-1">or</span>
                      <a
                        href="https://quickadmit.com/contact"
                        target="_blank"
                        class="has-text-link is-size-7"
                      >
                        Talk to Sales
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr />

          <div>
            <InquiryTable
              :options="inqOptions"
              :archivedCount="archivedCount"
              :filter-key="filterKey"
              :current-page="currentPage"
              :page-size="pageSize"
              :total-items="totalItems"
              :loading="loading"
              @page-change="handlePageChange"
              @bulkremove="fetchInquiries"
              @bulkarchive="fetchInquiries"
              @search-changed="handleSearchChange"
              @archive-toggle-change="handleArchiveToggle"
              @single-delete="handleSingleDelete"
              @single-archive="handleSingleArchive"
              @sort-changed="handleSortChange"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>

import InquiryTable from '../components/InquiryTable.vue';
import request from '../request';
import { mapGetters } from 'vuex';

export default {
  name: 'InquiryList',
  components: {
    InquiryTable,
  },
  data() {
    return {
      currentPage: 1, // Current page for pagination
      pageSize: 15, // Number of items per page
      totalItems: 0, // Number of total items
      selectedUserID: 0,
      selectedProviderID: 0,
      inqOptions: [],
      archivedCount: 0,
      userOptions: [],
      providerOptions: [],
      loading: true,
      selectedStatus: ['pending', 'complete', 'max-retry', 'unknown', 'expired', 'cancel', 'draft'],
      search: null,
      searchableFields: [],
      subscriptionNoticeClosed: false,
      archivedToggle: false,
      sortField: null,
      sortOrder: null,
    };
  },
  created() {
    this.initializePage();
  },
  methods: {
    async initializePage() {
      this.loading = true;

      try {
        const inquiriesRequest = this.fetchInquiries(false);
        const [usersResp, providersResp] = await Promise.all([
          request('/users/compact'),
          request('/providers/compact'),
          inquiriesRequest,
        ]);

        usersResp.data.users.sort((a, b) =>
          a.first_name.localeCompare(b.first_name)
        );
        this.userOptions = usersResp.data.users;

        providersResp.data.providers.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        this.providerOptions = providersResp.data.providers;
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to initialize page',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    goToSubscriptionPage() {
      this.$router.push({
        path: '/organization/settings/plan',
      });
    },
    handleUpdatePayment() {
      this.loading = true;
      request.post('/subscriptions/portal-session').then((resp) => {
        window.location.href = resp.data.redirect_url;
      });
    },
    closeSubscriptionNotice() {
      this.subscriptionNoticeClosed = true;
    },
    handleSelect() {
      this.loading = true;
      this.fetchInquiries().finally(() => (this.loading = false));
    },
    fetchInquiries(manageLoading = true) {
      if (manageLoading) this.loading = true;
      let args = {
        page: this.currentPage,
        limit: this.pageSize,
        is_grouped: true,
      };
      if (this.selectedUserID != 0) {
        args['user_id'] = this.selectedUserID;
      }
      if (this.selectedProviderID != 0) {
        args['provider_id'] = this.selectedProviderID;
      }
      if (this.selectedStatus && this.selectedStatus.length > 0) {
        args['status'] = this.selectedStatus.join(',');
      }

      // iterate over the search fields and add them to the args if user has entered a search term
      if (this.search && this.searchableFields && this.searchableFields.length > 0) {
        this.searchableFields.forEach((field) => {
          args[field] = this.search;
        });
      }

      if (!this.archivedToggle) {
        args['is_archived'] = false;
      }

      if (this.sortField) {
        args['sort_field'] = this.sortField;
        args['sort_order'] = this.sortOrder;
      }

      let params = new URLSearchParams(args).toString();
      return request(`/inquiries?${params}`)
        .then((resp) => {
          this.inqOptions = resp.data.inquiries;
          this.totalItems = Number(resp.headers['x-total-count']);
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$buefy.toast.open({
              message: 'Session expired. Please log in again.',
              type: 'is-danger',
              duration: 5000,
            });
            this.$router.push({ name: 'login' });
          } else {
            this.$buefy.toast.open({
              message: 'An error occurred. Please try again later.',
              type: 'is-danger',
              duration: 5000,
            });
          }
        })
        .finally(() => {
          if (manageLoading) this.loading = false;
        });
    },
    async fetchArchivedCount(manageLoading = true) {
      if (manageLoading) this.loading = true;
      let args = {
        is_grouped: true,
      };
      if (this.selectedUserID != 0) {
        args['user_id'] = this.selectedUserID;
      }
      if (this.selectedProviderID != 0) {
        args['provider_id'] = this.selectedProviderID;
      }
      if (this.selectedStatus && this.selectedStatus.length > 0) {
        args['status'] = this.selectedStatus.join(',');
      }

      if (this.search && this.searchableFields && this.searchableFields.length > 0) {
        this.searchableFields.forEach((field) => {
          args[field] = this.search;
        });
      }

      if (!this.search) {
        this.archivedCount = 0;
        if (manageLoading) this.loading = false;
        return;
      }
      args['is_archived'] = true;

      let params = new URLSearchParams(args).toString();
      return request(`/inquiries/count?${params}`)
        .then((resp) => {
          this.archivedCount = resp.data.count;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$buefy.toast.open({
              message: 'Session expired. Please log in again.',
              type: 'is-danger',
              duration: 5000,
            });
            this.$router.push({ name: 'login' });
          } else {
            this.$buefy.toast.open({
              message: 'An error occurred. Please try again later.',
              type: 'is-danger',
              duration: 5000,
            });
          }
        })
        .finally(() => {
          if (manageLoading) this.loading = false;
        });
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchInquiries();
    },
    handleSortChange({ field, order }) {
      this.sortField = field;
      this.sortOrder = order;
      this.currentPage = 1;
      this.fetchInquiries();
    },
    async handleSearchChange({ search, fields }) {
      this.loading = true;
      this.search = search;
      this.searchableFields = fields;
      try {
        await Promise.all([this.fetchInquiries(false), this.fetchArchivedCount(false)]);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'An error occurred. Please try again later.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
    handleArchiveToggle(payload) {
      this.archivedToggle = !!payload.event;
      this.fetchInquiries();
    },
    handleSingleDelete(row) {
      this.$buefy.dialog.confirm({
        title: 'Delete Inquiry',
        message: 'Are you sure you want to delete this inquiry?',
        confirmText: 'Delete',
        type: 'is-danger',
        onConfirm: () => {
          this.loading = true;
          request('/inquiries', {
            method: 'PUT',
            data: { inquiry_ids: [row.id], is_hidden: true },
          })
            .then(() => {
              this.$buefy.toast.open({
                message: 'Successfully deleted inquiry',
                type: 'is-success',
                duration: 5000,
              });
              this.fetchInquiries();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to delete inquiry',
                type: 'is-danger',
                duration: 5000,
              });
              this.loading = false;
            });
        },
      });
    },
    handleSingleArchive(row) {
      const isArchived = row.is_archived;
      const action = isArchived ? 'Unarchive' : 'Archive';

      this.$buefy.dialog.confirm({
        title: `${action} Inquiry`,
        message: `Are you sure you want to ${action.toLowerCase()} this inquiry?`,
        confirmText: action,
        type: isArchived ? 'is-success' : 'is-warning',
        onConfirm: () => {
          this.loading = true;
          request('/inquiries/archive', {
            method: 'PUT',
            data: { inquiry_ids: [row.id], is_archived: !isArchived },
          })
            .then(() => {
              this.$buefy.toast.open({
                message: `Successfully ${action.toLowerCase()}d inquiry`,
                type: 'is-success',
                duration: 5000,
              });
              this.fetchInquiries();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || `Failed to ${action.toLowerCase()} inquiry`,
                type: 'is-danger',
                duration: 5000,
              });
              this.loading = false;
            });
        },
      });
    },
  },
  computed: {
    ...mapGetters(['isManager', 'user']),
    // Passed to InquiryTable so it drops its cross-page selection when the
    // result set changes underneath it. Paging is deliberately not part of it.
    filterKey() {
      return [this.selectedUserID, this.selectedProviderID, this.selectedStatus.join(',')].join('|');
    },
    selectedUser() {
      return this.userOptions.find((opt) => opt.id === this.selectedUserID);
    },
    isPastDue() {
      return this.user && this.user.has_past_due_subscription;
    },
    canCreateInquiry() {
      if (!this.user) return false;

      // Can create if they have a subscription (active or past due)
      // Having a subscription means they have a base inquiry plan
      return this.user.has_active_subscription || this.user.has_past_due_subscription;
    },
    inquiryNoticeMessage() {
      if (this.loading || !this.user) {
        return null;
      }

      // If active subscription, no notice needed
      if (this.user.has_active_subscription) {
        return null;
      }

      // If past due, show simple message
      if (this.user.has_past_due_subscription) {
        return 'Subscription past due.';
      }

      return "You're not subscribed or your subscription is inactive";
    },
  },
};
</script>

<style scoped>
.level {
  flex-wrap: wrap;
  gap: 1rem;
}

.level-right {
  flex-wrap: wrap;
  gap: 1rem;
}

.level-item {
  flex-shrink: 0;
}

.subscription-wrapper {
  position: relative;
}

.subscription-notice {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #fef7f7;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  z-index: 10;
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 250px;
}

.subscription-notice p {
  margin-bottom: 0.5rem;
}

.subscription-notice p:last-child {
  margin-bottom: 0;
}

.subscription-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mx-1 {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}

.notice-close-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
}

/* Mobile responsive layout */
@media (max-width: 1023px) {
  .level {
    display: flex;
    flex-direction: column;
    align-items: stretch !important;
  }

  .level-left {
    display: none;
  }

  .level-right {
    display: flex;
    flex-wrap: wrap;
    width: 100% !important;
    gap: 0.75rem;
    margin-left: 0 !important;
    justify-content: stretch;
  }

  /* Create button - show first and full width */
  .level-right .level-item:last-child {
    order: -1;
    width: 100%;
    flex-basis: 100%;
    margin: 0 !important;
  }

  .level-right .level-item:last-child .subscription-wrapper {
    width: 100%;
  }

  .level-right .level-item:last-child .subscription-wrapper > .button {
    width: 100%;
  }

  /* Filter selects - side by side, each taking half width */
  .level-right .filter-select-item {
    flex: 1 1 calc(50% - 0.375rem);
    min-width: 0;
    margin: 0 !important;
    max-width: none;
  }

  .level-right .filter-select-item :deep(.field) {
    width: 100%;
    margin-bottom: 0;
  }

  .level-right .filter-select-item :deep(.control),
  .level-right .filter-select-item :deep(.select) {
    width: 100% !important;
    max-width: none !important;
  }

  .level-right .filter-select-item :deep(.select select) {
    width: 100% !important;
    max-width: none !important;
  }
}
</style>
